import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { registerServiceWorker } from "./lib/serviceWorker";
import { initSyncManager, refreshLocalCache } from "./lib/syncManager";

// V7.9: Register Service Worker for offline support
registerServiceWorker();

// V7.9: Initialize sync manager for offline/online transitions
initSyncManager();

// V7.9: Refresh local cache on startup (non-blocking)
setTimeout(() => refreshLocalCache(), 5000);

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

// V14 staging admin bootstrap: token is never bundled; it is supplied once via ?adminKey= and kept in sessionStorage.
try {
  const url = new URL(window.location.href);
  const key = url.searchParams.get("adminKey");
  if (key) {
    sessionStorage.setItem("ella_admin_token", key);
    url.searchParams.delete("adminKey");
    window.history.replaceState({}, "", url.toString());
  }
} catch {}

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      headers() {
        const token = sessionStorage.getItem("ella_admin_token");
        return token ? { "x-ella-admin-token": token } : {};
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
